import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';
import { CLUB_SENDER_ID, clockLabel, groupByDay } from '../../engine/messaging';
import {
  usePadMessaging,
  PAD_MESSAGES_POLL_IDLE_MS,
  PAD_MESSAGES_POLL_OPEN_MS,
} from '../../state/usePadMessaging';
import { autoEmoji, autoEmojiAt } from '../../lib/emoticons';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface BanepadContactSheetProps {
  /** The court this pad is scoped to — the pad writes as «Bane N». */
  courtId: number;
  /** Display name of the court («Bane 3» / nickname). */
  courtName?: string;
  /** The centre that owns the courts (the recipient). */
  clubName?: string;
}

/** Quick messages a court usually needs to send. */
export const PAD_QUICK_MESSAGES: { emoji: string; text: string }[] = [
  { emoji: '💡', text: 'Lyset på banen har gått.' },
  { emoji: '🕸️', text: 'Nettet må justeres.' },
  { emoji: '🧹', text: 'Banen må tørkes / ryddes.' },
  { emoji: '🔒', text: 'Døra eller låsen virker ikke.' },
  { emoji: '🎾', text: 'Vi mangler baller.' },
  { emoji: '🆘', text: 'Trenger hjelp på banen nå.' },
];

// «Kontakt senteret» on the court pad: a floating button (with an unread badge
// when the centre has replied) that opens a dark sheet in the pad's own look.
// Quick messages cover the usual («lyset har gått»), free text covers the
// rest; the pad writes as the court (sender court:N) into its club
// conversation, so the centre sees «Bane 3» in Meldinger and can answer back.
export function BanepadContactSheet({ courtId, courtName, clubName }: BanepadContactSheetProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentHint, setSentHint] = useState('');
  const uid = safeId('pad-contact', useId());
  // ONE CONVERSATION, FROM THE SERVER (#333). This used to read the club's
  // whole `messages_v1` blob through the generic Storage and filter to the
  // court's conversation in the browser — every conversation in the club, on a
  // tablet in a corridor. The court comes from the device record now, so there
  // is nothing here to point at another one.
  const messaging = usePadMessaging({
    pollMs: open ? PAD_MESSAGES_POLL_OPEN_MS : PAD_MESSAGES_POLL_IDLE_MS,
  });
  const unread = messaging.unread;
  const messages = messaging.messages;
  const days = useMemo(() => groupByDay(messages, Date.now()), [messages]);
  const endRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const label = courtName || `Bane ${courtId}`;
  const off = messaging.disabled;
  const centre = clubName || 'senteret';

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: 'end' });
  }, [open, messages.length]);

  // Opening the sheet reads the centre's replies.
  useEffect(() => {
    if (open && unread > 0) void messaging.markRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, unread]);

  const send = async (text: string) => {
    const body = autoEmoji(text.trim());
    if (!body || busy) return;
    setBusy(true);
    try {
      // The server opens the conversation on first use — the pad does not
      // create one, and could not name it if it did.
      await messaging.send(body);
      setDraft('');
      setSentHint('Sendt til ' + centre);
      setTimeout(() => setSentHint(''), 3000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-2 right-2 flex items-center gap-1.5 rounded-full font-semibold"
        style={{
          zIndex: 60,
          padding: '8px 14px',
          fontSize: 13,
          background: open ? '#e6c668' : 'rgba(15,23,42,0.85)',
          color: open ? '#0a0b10' : '#e6fbff',
          border: '1px solid rgba(230,198,104,0.5)',
        }}
        aria-label={unread > 0 ? `Kontakt senteret (${unread} uleste)` : 'Kontakt senteret'}
        aria-expanded={open}
        data-testid="pad-contact-button"
      >
        <MessageCircle size={16} />
        {off ? 'Meldinger av' : 'Kontakt senteret'}
        {unread > 0 && (
          <span
            className="font-mono font-bold rounded-full flex items-center justify-center"
            style={{
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              fontSize: 10,
              background: '#c2f02f',
              color: '#0a0b10',
            }}
            data-testid="pad-contact-badge"
          >
            {unread}
          </span>
        )}
      </button>

      {open && (
        // A `Dialog` (#900). The role and `aria-modal` sat on the BACKDROP —
        // the surface a tap closes it with — and nothing kept focus inside.
        // Same sheet, same size on the tablet; named by its own title, Escape
        // closes it as a tap outside does, and focus goes back to «Kontakt
        // senteret», which stays above the backdrop as before.
        //
        // THE FOCUS RING IS WHITE in here: the app's ring is ink, drawn for
        // light grounds, and this sheet is dark — the same reason the court
        // screen paints its own (index.css).
        <Dialog
          labelledBy={`${uid}-title`}
          describedBy={`${uid}-from`}
          onClose={() => setOpen(false)}
          showClose={false}
          backdropClassName="fixed inset-0 flex items-end justify-end p-3"
          backdropStyle={{ zIndex: 59, background: 'rgba(10,11,16,0.55)' }}
          className="flex flex-col rounded-2xl overflow-hidden [&_:focus-visible]:[outline-color:#ffffff]"
          style={{
            width: 440,
            maxWidth: '100%',
            maxHeight: 'calc(100dvh - 5rem)',
            background: 'linear-gradient(165deg, #14161f 0%, #0a0b10 100%)',
            color: '#e6fbff',
            border: '1px solid rgba(230,198,104,0.35)',
            marginBottom: 44,
          }}
        >
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div>
              <div id={`${uid}-title`} className="font-display font-bold" style={{ fontSize: 18 }}>
                Melding til {centre}
              </div>
              <div
                id={`${uid}-from`}
                className="font-mono text-[11px]"
                style={{ color: '#e6c668' }}
              >
                Fra {label} · banepad
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Lukk"
              className="p-1"
              style={{ color: '#8a948c' }}
            >
              <X size={18} />
            </button>
          </div>

          {off && (
            <div className="px-4 py-6 text-center" data-testid="pad-messaging-off">
              <div className="text-[14px] font-semibold">Meldinger er slått av på denne paden</div>
              <p className="text-[12px] mt-1" style={{ color: '#8a948c' }}>
                Banen sender og mottar ingen meldinger. Slå på igjen når du vil.
              </p>
              <button
                onClick={() => void messaging.setDisabled(false)}
                className="mt-4 rounded-full font-semibold text-[13px] px-5 py-2.5"
                style={{ background: '#c2f02f', color: '#0a0b10' }}
              >
                Slå på meldinger
              </button>
            </div>
          )}
          {!off && (
            <>
              {/* Quick messages */}
              <div className="px-4 pt-3 pb-2 flex flex-wrap gap-1.5">
                {PAD_QUICK_MESSAGES.map((q) => (
                  <button
                    key={q.text}
                    onClick={() => void send(q.text)}
                    disabled={busy}
                    className="rounded-full text-[12px] font-semibold px-3 py-1.5 disabled:opacity-40"
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.14)',
                      color: '#e6fbff',
                    }}
                  >
                    {q.emoji} {q.text}
                  </button>
                ))}
              </div>

              {/* Thread */}
              <div
                className="flex-1 overflow-y-auto px-4 py-2 space-y-2"
                style={{ minHeight: 120 }}
              >
                {days.length === 0 && (
                  <div className="text-center text-[12px] py-4" style={{ color: '#8a948c' }}>
                    Ingen meldinger ennå. Velg en hurtigmelding, eller skriv selv.
                  </div>
                )}
                {days.map((d) => (
                  <div key={d.dayKey} className="space-y-1.5">
                    <div
                      className="text-center font-mono text-[10px] uppercase tracking-wider py-1"
                      style={{ color: '#8a948c' }}
                    >
                      {d.label}
                    </div>
                    {d.messages.map((m) => {
                      const mine = m.senderId !== CLUB_SENDER_ID;
                      return (
                        <div
                          key={m.id}
                          className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className="max-w-[80%] rounded-2xl px-3 py-2 text-[13px] leading-snug whitespace-pre-wrap break-words"
                            style={
                              mine
                                ? { background: '#e6c668', color: '#0a0b10' }
                                : { background: 'rgba(255,255,255,0.1)', color: '#e6fbff' }
                            }
                          >
                            {m.text}
                            <span
                              className="block text-right font-mono text-[9px] mt-0.5"
                              style={{ opacity: 0.6 }}
                            >
                              {mine ? label : centre} · {clockLabel(m.sentAt)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div ref={endRef} />
              </div>

              {/* Composer */}
              <div
                className="flex items-end gap-2 px-3 py-2"
                style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
              >
                <textarea
                  ref={inputRef}
                  className="flex-1 rounded-xl px-3 py-2 text-[13px] resize-none"
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    color: '#e6fbff',
                    border: '1px solid rgba(255,255,255,0.14)',
                  }}
                  rows={2}
                  placeholder="Skriv en melding til senteret …"
                  value={draft}
                  onChange={(e) => {
                    // «:)» → 😊 while typing (whole tokens only — see lib/emoticons).
                    const el = e.target;
                    const { text, caret } = autoEmojiAt(
                      el.value,
                      el.selectionStart ?? el.value.length,
                    );
                    setDraft(text);
                    if (text !== el.value)
                      requestAnimationFrame(() =>
                        inputRef.current?.setSelectionRange(caret, caret),
                      );
                  }}
                  aria-label="Skriv en melding til senteret"
                />
                <button
                  onClick={() => void send(draft)}
                  disabled={!draft.trim() || busy}
                  className="flex items-center justify-center rounded-full disabled:opacity-40"
                  style={{ width: 40, height: 40, background: '#c2f02f', color: '#0a0b10' }}
                  aria-label="Send"
                >
                  <Send size={17} />
                </button>
              </div>
              {sentHint && (
                <div className="text-center text-[11px] pb-2" style={{ color: '#c2f02f' }}>
                  ✓ {sentHint}
                </div>
              )}
              <div className="text-center pb-3">
                <button
                  onClick={() => void messaging.setDisabled(true)}
                  className="text-[11px] underline"
                  style={{ color: '#8a948c' }}
                >
                  Slå av meldinger for denne banen
                </button>
              </div>
            </>
          )}
        </Dialog>
      )}
    </>
  );
}
